import { useState, useEffect, useCallback, useRef } from "react";
import Table from "../Table/Table";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { Bounce, toast } from "react-toastify";
import { buildFilterQueryParams } from "../utils/queryHelpers";
import useDebouncedValue from "../hooks/useDebouncedValue";

const Feedback = () => {
  const [loading, setLoading] = useState(false);
  const [feedbacks, setFeedbacks] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnFilterFns, setColumnFilterFns] = useState({});
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const debouncedSorting = useDebouncedValue(sorting, 400);
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 400);
  const downloadRef = useRef(null);

  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const isoDatePart = dateString.slice(0, 10);
    const [year, month, day] = isoDatePart.split("-");
    if (!year || !month || !day) return "";

    return `${day}.${month}.${year}`;
  };

  const fetchFeedbacks = useCallback(
    async ({
      pageParam = page,
      sizeParam = pageSize,
      sortingParam = debouncedSorting,
      filtersParam = debouncedColumnFilters,
      filterFnsParam = columnFilterFns,
    } = {}) => {
      setLoading(true);
      try {
        const sortStrings = (sortingParam ?? []).map(
          ({ id, desc }) => `${id},${desc ? "desc" : "asc"}`
        );

        const filterParams = buildFilterQueryParams(
          filtersParam,
          filterFnsParam
        );

        const params = {
          page: pageParam,
          size: sizeParam,
          ...filterParams,
        };

        if (sortStrings.length > 0) {
          params.sort = sortStrings.join(";");
        }

        const response = await api.get(`/feedback`, {
          params,
        });
        if (response.status === 200) {
          const { content = [], page: pageInfo } = response.data ?? {};

          setFeedbacks(content);
          setTotalRows(pageInfo?.totalElements ?? content.length);
        }
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, debouncedSorting, debouncedColumnFilters, columnFilterFns]
  );

  useEffect(() => {
    const role = localStorage.getItem("userRole");
    if (!role) {
      alert("Accedi come amministratore per visualizzare i feedback!");
      navigate("/login");
    } else {
      fetchFeedbacks();
    }
  }, [navigate, fetchFeedbacks]);

  const handlePageChange = (pageNum) => {
    setPage(Math.max(pageNum - 1, 0));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSortingChange = (newSorting) => {
    setSorting(newSorting);
    setPage(0);
  };

  const handleColumnFiltersChange = (newFilters) => {
    setColumnFilters(newFilters);
    setPage(0);
  };

  const handleColumnFilterFnsChange = (newFilterFns) => {
    setColumnFilterFns(newFilterFns);
    setPage(0);
  };

  const handleRowClick = (feedback) => {
    const codiceFiscale = feedback.trainer?.codiceFiscale;
    if (!codiceFiscale) {
      toast.error("Codice fiscale non disponibile per questo docente.");
      return;
    }
    navigate(`/feedback/${feedback.idFeedback}`, {
      state: { codiceFiscale },
    });
  };

  const columns = [
    { field: "trainer.codiceFiscale", header: "CF docente" },
    { field: "trainer.nome", header: "Nome docente" },
    { field: "trainer.cognome", header: "Cognome docente" },
    { field: "punteggioTotale", header: "Punteggio Totale" },
    { field: "feedbackCoinvolgimento", header: "Coinvolgimento" },
    { field: "feedbackPartecipanti", header: "Partecipanti" },
    { field: "feedbackTutor", header: "Tutor" },
    { field: "feedbackEfficacia", header: "Efficacia" },
    { field: "data", header: "Data Inserimento" },
    { field: "autore", header: "Autore" },
  ];

  const processedData = feedbacks.map((item) => ({
    ...item,
    "trainer.codiceFiscale": item.trainer?.codiceFiscale || "",
    "trainer.nome": item.trainer?.nome || "",
    "trainer.cognome": item.trainer?.cognome || "",
    data: formatDate(item.dataInserimento) || "",
  }));

  const handleExport = async (format) => {
    setIsDownloadOpen(false);
    setLoading(true);
    try {
      const response = await api.get("/export/feedback", {
        params: { format },
        responseType: "blob",
      });
      
      if (response.status === 204) {
        toast.info("Non ci sono dati da scaricare", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        return;
      }
      
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const contentDisposition = response.headers["content-disposition"];
      let filename = `feedback.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Errore durante il download", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setIsDownloadOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="content-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="page-header">
        <div className="page-title">Feedback</div>
        <div className="controls">
          <div className="download-menu" ref={downloadRef}>
            <button
              className="download-btn"
              onClick={() => setIsDownloadOpen((prev) => !prev)}
            >
              Scarica
            </button>
            {isDownloadOpen && (
              <div className="download-dropdown">
                <button onClick={() => handleExport("csv")}>CSV</button>
                <button onClick={() => handleExport("xlsx")}>Excel</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="page-content feedback">
        <Table
          columns={columns}
          data={processedData}
          onRowClick={handleRowClick}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          totalRows={totalRows}
          perPage={pageSize}
          currentPage={page + 1}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          columnFilterFns={columnFilterFns}
          onColumnFilterFnsChange={handleColumnFilterFnsChange}
        />
      </div>
    </div>
  );
};

export default Feedback;
